import { Label } from "@/ui/primitives/label";
import Button from "@imminently/decisively-ui/Button";
import DateTimeField from "@imminently/decisively-ui/DateTimeField";
import DateTimeInput from "@imminently/decisively-ui/DateTimeInput";
import DateTimePicker from "@imminently/decisively-ui/DateTimePicker";
import Form, { FieldWatch } from "@imminently/decisively-ui/Form";
import Select from "@imminently/decisively-ui/Select";
import Switch from "@imminently/decisively-ui/Switch";
import { TIME_PRESETS } from "@imminently/decisively-ui/TimeInput";
import { format } from "date-fns";
import * as React from "react";
import {
  ComponentPage,
  ComponentPreview,
  ComponentSection,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage";
import {
  KeyboardReference,
  LOCALE_OPTIONS,
  PRESET_LAYOUT_OPTIONS,
  PRESET_SETS,
  PRESET_SET_OPTIONS,
  STEP_OPTIONS,
  resolveLocale,
  resolveStep,
} from "../components/DateTimeTestInputs";

const KEYBOARD_ROWS: [string, string][] = [
  ["Tab", "Move from the date input to the time input. Each keeps its own keyboard behaviour."],
  ["Date: ArrowDown", "Open the calendar (see Date Picker)."],
  ["Time: ArrowDown / ArrowUp", "Open and move through the time list (see Time Picker)."],
  ["Time: ArrowLeft / ArrowRight", "Switch between the presets and times columns once moving through the list."],
  ["Enter / Escape", "Commit / close in whichever input has focus."],
  ["Ctrl/Cmd + V", "Pasting a full date and time into either part fills both."],
];

const DEFAULT_TIME_OPTIONS = {
  startOfDay: TIME_PRESETS.startOfDay.time,
  endOfDay: TIME_PRESETS.endOfDay.time,
};

const PASTE_SAMPLES = [
  "2026-12-25T10:30:00.000Z",
  "2026-12-25T10:30:00+10:00",
  "2026-12-25 21:30",
  "25/12/2026 9:30pm",
];

const VALIDITY_PRESETS = [TIME_PRESETS.startOfDay, TIME_PRESETS.endOfDay];

const formatValue = (value: Date | null) => (value ? format(value, "EEE d MMM yyyy, HH:mm:ss") : "null");

function ValueReadout({ value }: { value: Date | null }) {
  return (
    <code className="text-xs text-muted-foreground">
      {formatValue(value)}
      {value ? ` | ${value.toISOString()}` : ""}
    </code>
  );
}

export function DateTimePickerPage() {
  const [value, setValue] = React.useState<Date | null>(null);
  const [locale, setLocale] = React.useState("browser");
  const [hourFormat, setHourFormat] = React.useState("12");
  const [step, setStep] = React.useState("15");
  const [presets, setPresets] = React.useState("dayBounds");
  const [presetLayout, setPresetLayout] = React.useState<"side" | "stacked">("side");
  const [variant, setVariant] = React.useState<"separate" | "combined">("separate");
  const [defaultTime, setDefaultTime] = React.useState<keyof typeof DEFAULT_TIME_OPTIONS>("startOfDay");
  const [disabled, setDisabled] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [fullWidth, setFullWidth] = React.useState(false);

  const [validFrom, setValidFrom] = React.useState<Date | null>(null);
  const [validTo, setValidTo] = React.useState<Date | null>(null);
  const [pickerValue, setPickerValue] = React.useState<Date | null>(null);

  const hour12 = hourFormat === "locale" ? undefined : hourFormat === "12";
  // Same rule as OrgModelFormDialog: Valid To must be strictly after Valid From.
  const rangeError = Boolean(validFrom && validTo && validFrom >= validTo);

  return (
    <ComponentPage
      title="Date Time Picker"
      description="DateTimeInput orchestrates a DateInput and a TimeInput over one value, splitting it for each control."
    >
      <ComponentSection
        title="Playground"
        description="Picking a date with no time fills in the default time. A time entered before a date is kept until a date is picked."
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview className="flex-col items-stretch gap-2">
            <DateTimeInput<Date | null>
              aria-label="Date"
              serialize={false}
              value={value}
              onValueChange={setValue}
              locale={resolveLocale(locale)}
              hour12={hour12}
              variant={variant}
              timeStepMinutes={resolveStep(step)}
              timePresets={PRESET_SETS[presets]?.presets}
              timePresetLayout={presetLayout}
              defaultTime={DEFAULT_TIME_OPTIONS[defaultTime]}
              disabled={disabled}
              error={error}
              fullWidth={fullWidth}
            />
            <ValueReadout value={value} />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                label="Set externally: 25 Dec 2026 9:30 pm"
                onClick={() => setValue(new Date(2026, 11, 25, 21, 30))}
              />
              <Button
                size="sm"
                variant="outline"
                label="Set externally: null"
                onClick={() => setValue(null)}
              />
            </div>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Variant">
              <Select
                value={variant}
                onValueChange={(next) => setVariant(next === "combined" ? "combined" : "separate")}
                options={[
                  { value: "separate", label: "Separate inputs" },
                  { value: "combined", label: "Combined field" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Locale">
              <Select
                value={locale}
                onValueChange={(next) => setLocale(next ?? "browser")}
                options={LOCALE_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Hour format">
              <Select
                value={hourFormat}
                onValueChange={(next) => setHourFormat(next ?? "12")}
                options={[
                  { value: "locale", label: "Locale default" },
                  { value: "12", label: "12 hour" },
                  { value: "24", label: "24 hour" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Time step">
              <Select
                value={step}
                onValueChange={(next) => setStep(next ?? "15")}
                options={STEP_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Default time">
              <Select
                value={defaultTime}
                onValueChange={(next) => setDefaultTime((next ?? "startOfDay") as keyof typeof DEFAULT_TIME_OPTIONS)}
                options={[
                  { value: "startOfDay", label: "Start of day" },
                  { value: "endOfDay", label: "End of day" },
                ]}
              />
            </ControlRow>
            <ControlRow label="Time presets">
              <Select
                value={presets}
                onValueChange={(next) => setPresets(next ?? "none")}
                options={PRESET_SET_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Preset layout">
              <Select
                value={presetLayout}
                onValueChange={(next) => setPresetLayout(next === "stacked" ? "stacked" : "side")}
                options={PRESET_LAYOUT_OPTIONS}
              />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch
                checked={disabled}
                onCheckedChange={setDisabled}
              />
            </ControlRow>
            <ControlRow label="Error">
              <Switch
                checked={error}
                onCheckedChange={setError}
              />
            </ControlRow>
            <ControlRow label="Full width">
              <Switch
                checked={fullWidth}
                onCheckedChange={setFullWidth}
              />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      <ComponentSection title="Keyboard">
        <KeyboardReference rows={KEYBOARD_ROWS} />
      </ComponentSection>

      <ComponentSection
        title="Variants"
        description='variant="separate" (default) renders two inputs; variant="combined" renders one field with a separator. Each part keeps its own picker either way.'
      >
        <ComponentPreview className="flex-col items-stretch justify-start gap-4">
          <VariantGrid columns={2}>
            <VariantItem label="separate">
              <DateTimeInput
                aria-label="Separate"
                value="2026-12-25 09:00"
                hour12={hour12}
              />
            </VariantItem>
            <VariantItem label="combined">
              <DateTimeInput
                aria-label="Combined"
                value="2026-12-25 09:00"
                variant="combined"
                hour12={hour12}
              />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Paste"
        description="Paste a full date and time into either part of the playground. ISO timestamps with a zone are converted to local time; text without a time pastes normally."
      >
        <ComponentPreview className="flex-col items-stretch justify-start gap-2">
          {PASTE_SAMPLES.map((sample) => (
            <div
              key={sample}
              className="flex items-center gap-4 text-sm"
            >
              <code className="w-64">{sample}</code>
              <Button
                size="sm"
                variant="ghost"
                label={`Copy ${sample}`}
                onClick={() => navigator.clipboard?.writeText(sample)}
              >
                Copy
              </Button>
            </div>
          ))}
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Valid From / Valid To"
        description="The org models use case. Valid From defaults to the start of the day and Valid To to the end of the day."
      >
        <ComponentPreview className="flex-col items-stretch justify-start gap-4">
          <div className="grid gap-1.5">
            <Label>Valid From</Label>
            <DateTimeInput<Date | null>
              aria-label="Valid From"
              serialize={false}
              value={validFrom}
              onValueChange={setValidFrom}
              timePresets={VALIDITY_PRESETS}
              hour12={hour12}
            />
            <ValueReadout value={validFrom} />
          </div>
          <div className="grid gap-1.5">
            <Label>Valid To</Label>
            <DateTimeInput<Date | null>
              aria-label="Valid To"
              serialize={false}
              value={validTo}
              onValueChange={setValidTo}
              defaultTime={TIME_PRESETS.endOfDay.time}
              timePresets={VALIDITY_PRESETS}
              hour12={hour12}
              error={rangeError}
            />
            <ValueReadout value={validTo} />
            {rangeError ? <span className="text-sm text-destructive">Valid To must be after Valid From.</span> : null}
          </div>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Form field"
        description='DateTimeField stores "yyyy-MM-dd HH:mm" strings by default (with ":ss" when seconds are set). serialize={false} stores Dates.'
      >
        <ComponentPreview className="items-stretch justify-start">
          <Form
            className="grid w-full max-w-xl gap-4"
            defaultValues={{ startsAt: "2026-12-25 09:00", endsAt: new Date(2026, 11, 25, 23, 59, 59) }}
          >
            <>
              <DateTimeField
                name="startsAt"
                label="Starts at"
                fullWidth
              />
              <DateTimeField<Date | null>
                name="endsAt"
                label="Ends at"
                serialize={false}
                defaultTime={TIME_PRESETS.endOfDay.time}
                timePresets={VALIDITY_PRESETS}
                fullWidth
              />
              <FieldWatch names={["startsAt", "endsAt"]}>
                {(values) => <code className="text-xs text-muted-foreground">{JSON.stringify(values)}</code>}
              </FieldWatch>
            </>
          </Form>
        </ComponentPreview>
      </ComponentSection>

      <ComponentSection
        title="Custom trigger"
        description="DateTimePicker opens a calendar and a time list from any trigger. The time list is focusable and uses the same keys as TimeInput."
      >
        <ComponentPreview className="flex-col items-start gap-2">
          <DateTimePicker
            value={pickerValue}
            onValueChange={setPickerValue}
            timePresets={VALIDITY_PRESETS}
          >
            <DateTimePicker.Trigger asChild>
              <Button
                variant="outline"
                label="Choose date and time"
              >
                {pickerValue ? format(pickerValue, "d MMM yyyy, h:mm aaa") : "Choose date and time"}
              </Button>
            </DateTimePicker.Trigger>
            <DateTimePicker.Content />
          </DateTimePicker>
          <ValueReadout value={pickerValue} />
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  );
}
